/**
 * Selecting which protocol to hang.
 *
 * Replaces the v1 `orderOfMatching` score. This format has no priority field:
 * groups and categories are drag-reorderable in MedDream's settings UI and
 * matched in array order, first applicable wins. Order is data.
 *
 * ## Precedence
 *
 * 1. Skip inactive groups, and groups whose `devices` list excludes the current
 *    device. An empty `devices` means all devices.
 * 2. Evaluate `groupConditions`; a failing group is out.
 * 3. Within a surviving group, the first category whose `studyConditions` pass
 *    is that group's candidate.
 * 4. `skipIfOtherGroupHasApplicableHp` marks a group as a fallback: it is only
 *    used when no ordinary group produced a candidate. In the reference export
 *    the broad catch-all group ("All", no MG, no OPT) carries this flag, so the
 *    narrower MG/OPT groups win despite appearing later.
 *
 * Step 4 could not be read off MedDream's own matcher, but the Advance Viewer
 * confirms it: a fallback group drops out of its "Hanging protocols list" as
 * soon as any other group matches, rather than being listed last (observed on
 * study 8852, 2026-09-10). It is also the only reading consistent with the
 * reference export's ordering.
 */

import { evaluateRuleGroup } from "./ruleEngine";
import { DEVICE, GROUP_KIND } from "./protocolEnums";
import { fieldsUsed } from "./viewerContext";

export { DEVICE };

/**
 * Record what a condition slot asks for, before it is evaluated.
 *
 * Callers pass a `consultedFields` Set when they need to know whether the match
 * can be trusted — a caller that cannot source every field the walk touched may
 * have been handed a different protocol than one with full data would get. Only
 * slots actually reached are recorded: the walk stops at the first applicable
 * protocol, so what sits after it never influenced the answer.
 */
const consult = (consulted, conditions) => {
  if (consulted) fieldsUsed(conditions, consulted);
};

/** An empty `devices` list means the group applies everywhere. */
export function groupAllowsDevice(group, device) {
  const devices = group?.devices;
  if (!Array.isArray(devices) || devices.length === 0) return true;
  if (!device) return true;
  return devices.includes(device);
}

/**
 * The first category in `group` whose studyConditions pass, or null.
 * Returns the index too, since callers need it to address the category.
 */
export function findMatchingCategory(group, context, options, consulted = null) {
  const categories = group?.categories || [];
  for (let index = 0; index < categories.length; index += 1) {
    const category = categories[index];
    consult(consulted, category.studyConditions);
    if (evaluateRuleGroup(category.studyConditions, context, options)) {
      return { category, categoryIndex: index };
    }
  }
  return null;
}

/**
 * Pick the protocol to hang from a whole document.
 *
 * `context` is the flat field map the rule engine reads — see ruleEngine.js.
 * Returns `{ group, groupIndex, category, categoryIndex, fallback }` or null.
 *
 * Pass a Set as `consultedFields` to collect every field the walk read on its way
 * to the answer. See `consult` above for what that is good for.
 */
export function findApplicableProtocol(
  document,
  context,
  { device = null, kind = GROUP_KIND.STANDARD, consultedFields = null, ...options } = {}
) {
  const groups = document?.[kind] || [];

  let fallbackCandidate = null;

  for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
    const group = groups[groupIndex];
    if (!group || group.active === false) continue;
    if (!groupAllowsDevice(group, device)) continue;

    // Special groups (hpDrag / hpComparison) carry no groupConditions at all.
    if (group.groupConditions) {
      consult(consultedFields, group.groupConditions);
      if (!evaluateRuleGroup(group.groupConditions, context, options)) continue;
    }

    const match = findMatchingCategory(group, context, options, consultedFields);
    if (!match) continue;

    const candidate = { group, groupIndex, ...match };
    if (group.skipIfOtherGroupHasApplicableHp) {
      // Hold it — an ordinary group later in the list outranks it.
      if (!fallbackCandidate) fallbackCandidate = { ...candidate, fallback: true };
      continue;
    }
    return { ...candidate, fallback: false };
  }

  return fallbackCandidate;
}

/**
 * Every applicable protocol, in precedence order — what a "choose a hanging
 * protocol" menu should list.
 *
 * A fallback group is left out entirely as soon as an ordinary group produced a
 * candidate — the same rule findApplicableProtocol applies to the auto-hang, so
 * the menu can never offer a protocol the matcher has already ruled out. That is
 * what "used only when no other group has an applicable protocol" means in the
 * group editor, and what the Advance Viewer does: a fallback drops off its list
 * the moment anything else matches.
 */
export function listApplicableProtocols(document, context, { device = null, kind = GROUP_KIND.STANDARD, ...options } = {}) {
  const groups = document?.[kind] || [];
  const ordinary = [];
  const fallback = [];

  groups.forEach((group, groupIndex) => {
    if (!group || group.active === false) return;
    if (!groupAllowsDevice(group, device)) return;
    if (group.groupConditions && !evaluateRuleGroup(group.groupConditions, context, options)) return;

    (group.categories || []).forEach((category, categoryIndex) => {
      if (!evaluateRuleGroup(category.studyConditions, context, options)) return;
      const entry = {
        group,
        groupIndex,
        category,
        categoryIndex,
        fallback: Boolean(group.skipIfOtherGroupHasApplicableHp),
      };
      (entry.fallback ? fallback : ordinary).push(entry);
    });
  });

  return ordinary.length > 0 ? ordinary : fallback;
}
